import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Loader2, ShieldCheck, KeyRound, Smartphone, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { adminApi, adminTokenStorage } from '../lib/adminApi';
import { useToast } from '../hooks/use-toast';

export default function AdminLoginPage() {
  const [step, setStep] = useState('credentials'); // credentials | email_otp | mfa_setup | mfa
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [mfaSetup, setMfaSetup] = useState(null); // { secret, qrDataUrl }
  const [useBackup, setUseBackup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!adminTokenStorage.get()) { if (alive) setChecking(false); return; }
      try {
        await adminApi.get('/admin/me');
        if (alive) navigate('/admin', { replace: true });
      } catch (e) {
        adminTokenStorage.clear();
        if (alive) setChecking(false);
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  const err = (e, fallback) => toast({ title: 'Échec', description: e?.response?.data?.detail || fallback });

  const submitCredentials = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast({ title: 'Champs requis', description: 'Email et mot de passe sont obligatoires.' });
      return;
    }
    setLoading(true);
    try {
      const { data } = await adminApi.post('/admin/login', { email: email.trim(), password });
      setChallengeId(data.challengeId);
      setEmailHint(data.emailHint || '');
      setStep('email_otp');
      toast({ title: 'Code envoyé', description: data.emailMock ? `Mode dev — code : ${data.devEmailCode}` : `Un code a été envoyé à ${data.emailHint || 'votre email'}.` });
    } catch (e2) { err(e2, 'Identifiants invalides.'); }
    finally { setLoading(false); }
  };

  const submitEmailCode = async (e) => {
    e.preventDefault();
    if (emailCode.trim().length < 6) return;
    setLoading(true);
    try {
      const { data } = await adminApi.post('/admin/login/email', { challengeId, code: emailCode.trim() });
      if (data.step === 'mfa_setup') {
        setMfaSetup({ secret: data.secret, qrDataUrl: data.qrDataUrl });
        setStep('mfa_setup');
      } else {
        setStep('mfa');
      }
    } catch (e2) { err(e2, 'Code email incorrect.'); }
    finally { setLoading(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (mfaCode.trim().length < 6) return;
    setLoading(true);
    try {
      const { data } = await adminApi.post('/admin/login/mfa', { challengeId, code: mfaCode.trim() });
      adminTokenStorage.set(data.token);
      toast({ title: 'Connexion réussie', description: `Bienvenue ${data.admin?.email || ''}` });
      navigate('/admin', { replace: true });
    } catch (e2) { err(e2, "Code d'authentification incorrect."); }
    finally { setLoading(false); }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  const StepIcon = { credentials: Lock, email_otp: Mail, mfa_setup: Smartphone, mfa: KeyRound }[step] || ShieldCheck;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#f5f4ef]" data-testid="admin-login-page">
      <div className="w-full max-w-[440px] rounded-3xl bg-white border border-black/5 p-8 sm:p-10 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#1a1d1a] text-white flex items-center justify-center">
            <StepIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase font-medium text-neutral-500">Fodindin</div>
            <h1 className="text-[20px] font-semibold text-[#1a1d1a]">Administration</h1>
          </div>
        </div>

        {/* Step indicator */}
        <div className="mt-6 flex items-center gap-2" data-testid="admin-step-indicator">
          {['credentials', 'email_otp', 'mfa'].map((s, i) => {
            const active = ['credentials', 'email_otp', (step === 'mfa_setup' ? 'mfa_setup' : 'mfa')].indexOf(step) >= i
              || (step === 'mfa_setup' && s === 'mfa');
            return <div key={s} className={`h-1.5 flex-1 rounded-full ${active ? 'bg-[#d97757]' : 'bg-neutral-200'}`} />;
          })}
        </div>

        {step === 'credentials' && (
          <form onSubmit={submitCredentials} className="mt-6 space-y-4" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="text-[12.5px] font-medium text-neutral-700">Email administrateur</label>
              <div className="mt-1 relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input id="admin-email" data-testid="admin-email-input" type="email" autoComplete="username"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@fodindincagnotte.com" className="pl-9 h-11 rounded-xl" disabled={loading} />
              </div>
            </div>
            <div>
              <label htmlFor="admin-password" className="text-[12.5px] font-medium text-neutral-700">Mot de passe</label>
              <div className="mt-1 relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input id="admin-password" data-testid="admin-password-input" type="password" autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="pl-9 h-11 rounded-xl" disabled={loading} />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="admin-login-submit-btn"
              className="btn-anim w-full h-11 rounded-full bg-[#1a1d1a] hover:bg-black text-white text-[14px]">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Continuer
            </Button>
          </form>
        )}

        {step === 'email_otp' && (
          <form onSubmit={submitEmailCode} className="mt-6 space-y-4" data-testid="admin-email-otp-form">
            <p className="text-[13px] text-neutral-600">Entrez le code à 6 chiffres envoyé à <span className="font-medium">{emailHint}</span>.</p>
            <Input data-testid="admin-email-code-input" inputMode="numeric" maxLength={6}
              value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000" className="h-12 rounded-xl text-center text-xl tracking-[0.4em]" disabled={loading} autoFocus />
            <Button type="submit" disabled={loading || emailCode.length < 6} data-testid="admin-email-code-submit-btn"
              className="btn-anim w-full h-11 rounded-full bg-[#1a1d1a] hover:bg-black text-white">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Vérifier le code email
            </Button>
            <button type="button" onClick={() => setStep('credentials')} className="w-full text-center text-[12.5px] text-neutral-500 hover:text-black inline-flex items-center justify-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Recommencer
            </button>
          </form>
        )}

        {step === 'mfa_setup' && (
          <form onSubmit={submitMfa} className="mt-6 space-y-4" data-testid="admin-mfa-setup-form">
            <p className="text-[13px] text-neutral-600">Première connexion : scannez ce QR code avec <span className="font-medium">Google Authenticator</span> (ou Authy), puis entrez le code affiché.</p>
            {mfaSetup?.qrDataUrl && (
              <div className="flex justify-center">
                <img src={mfaSetup.qrDataUrl} alt="QR code MFA" data-testid="admin-mfa-qr" className="w-44 h-44 rounded-xl border border-black/5" />
              </div>
            )}
            <p className="text-[11px] text-neutral-500 text-center">Clé manuelle : <code className="font-mono text-neutral-700 break-all">{mfaSetup?.secret}</code></p>
            <Input data-testid="admin-mfa-code-input" inputMode="numeric" maxLength={6}
              value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000" className="h-12 rounded-xl text-center text-xl tracking-[0.4em]" disabled={loading} />
            <Button type="submit" disabled={loading || mfaCode.length < 6} data-testid="admin-mfa-submit-btn"
              className="btn-anim w-full h-11 rounded-full bg-[#1a1d1a] hover:bg-black text-white">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Activer & se connecter
            </Button>
          </form>
        )}

        {step === 'mfa' && (
          <form onSubmit={submitMfa} className="mt-6 space-y-4" data-testid="admin-mfa-form">
            <p className="text-[13px] text-neutral-600">Entrez le code à 6 chiffres de votre application d'authentification.</p>
            {!useBackup ? (
              <Input data-testid="admin-mfa-code-input" inputMode="numeric" maxLength={6}
                value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000" className="h-12 rounded-xl text-center text-xl tracking-[0.4em]" disabled={loading} autoFocus />
            ) : (
              <Input data-testid="admin-backup-code-input" maxLength={9}
                value={mfaCode} onChange={(e) => setMfaCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX" className="h-12 rounded-xl text-center text-lg tracking-[0.3em] font-mono" disabled={loading} autoFocus />
            )}
            <Button type="submit" disabled={loading || mfaCode.trim().length < 6} data-testid="admin-mfa-submit-btn"
              className="btn-anim w-full h-11 rounded-full bg-[#1a1d1a] hover:bg-black text-white">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Se connecter
            </Button>
            <button type="button" data-testid="admin-toggle-backup-btn" onClick={() => { setUseBackup(!useBackup); setMfaCode(''); }}
              className="w-full text-center text-[12px] text-neutral-500 hover:text-black">
              {useBackup ? "← Utiliser l'application d'authentification" : "Utiliser un code de secours"}
            </button>
            <button type="button" onClick={() => setStep('credentials')} className="w-full text-center text-[12.5px] text-neutral-500 hover:text-black inline-flex items-center justify-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Recommencer
            </button>
          </form>
        )}

        <p className="mt-6 text-[11.5px] text-neutral-500 text-center">
          Accès sécurisé à 3 facteurs (mot de passe · email · authentificateur). Toute tentative non autorisée est enregistrée.
        </p>
      </div>
    </div>
  );
}
